<template>
  <div class="my-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    <div v-for="post in posts" :key="post.id"
      class="relative rounded-lg overflow-hidden shadow hover:shadow-xl transition flex flex-col group border"
      :class="post.isPinned ? 'border-orange-500' : 'border-gray-200'">

      <!-- Card Image -->
      <div class="relative w-full h-56 cursor-pointer overflow-hidden rounded-t-lg">

        <img v-if="post.media_link && post.media_link !== ''" :src="`${imageDirectory}${post.media_link}`"
          alt="Post Media" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />

        <div v-else class="w-full h-full flex flex-col items-center justify-center bg-gray-200 text-gray-400">
          <svg class="w-10 h-10 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h18M3 12h18M3 17h18" />
          </svg>
          <span class="text-sm">No Image</span>
        </div>

        <!-- Top-right overlay: Pinned + Status -->
        <div class="absolute top-2 right-2 flex items-center gap-1 z-10">
          <!-- Top-right pinned indicator (red pin, no border) -->
          <div v-if="post.isPinned"
            class="w-5 h-5 bg-white/70 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg text-red-500 text-lg">
            📌
          </div>

          <!-- Status Pill -->
          <span v-if="post.status" class="text-xs font-semibold px-2 py-0.5 rounded-full shadow"
            :class="statusClass(post.status)">
            {{ formatStatus(post.status) }}
          </span>
        </div>

        <!-- Hover Overlay: Pin + Edit + Delete -->
        <div
          class="absolute inset-0 bg-white/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 flex items-center justify-center gap-3 transition duration-300 rounded-t-lg z-5">

          <button @click.stop="togglePin(post)"
            class="px-4 py-2 text-sm font-semibold rounded bg-orange-400 text-white hover:bg-orange-500 transition">
            {{ post.isPinned ? 'Unpin' : 'Pin' }}
          </button>

          <button @click="$emit('edit', post)"
            class="px-4 py-2 text-sm font-semibold rounded bg-blue-500 text-white hover:bg-blue-600 transition">
            Edit
          </button>

          <!-- <button @click.stop="openConfirmDelete(post)"
            class="bg-red-500 text-white px-3 py-1 rounded font-semibold hover:bg-red-600">
            Delete
          </button> -->
        </div>
      </div>

      <!-- Card Info -->
      <div class="bg-white p-3 rounded-b-lg border-t border-gray-100 flex flex-col justify-between">

        <!-- Title -->
        <h4 class="text-sm font-semibold text-gray-800 truncate mb-2">{{ post.title }}</h4>

        <!-- Timestamp -->
        <span class="text-gray-500 text-xs mt-1">
          {{

            post.updatedAt
              ? `Updated: ${new Date(post.updatedAt).toLocaleDateString()}`
              : post.createdAt
                ? `Created: ${new Date(post.createdAt).toLocaleDateString()}`
                : '—'
          }}
        </span>
      </div>
    </div>

    <!-- Confirmation Modal for Delete -->
    <ConfirmationModal v-if="showConfirmDelete" :show="showConfirmDelete" title="Confirm Deletion"
      :message="`Are you sure you want to delete '${postToDelete?.title}'?`" :loading="loading[postToDelete?.id]"
      confirm-text="Yes, Delete" cancel-text="Cancel" @confirm="confirmDelete" @cancel="closeConfirmDelete" />
  </div>
</template>

<script>
import { ref } from "vue";
import { updateModulePost, deleteModulePost } from "@/api/modulePosts";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

export default {
  name: "PostGridHoverActions",
  components: { ConfirmationModal },
  props: {
    posts: { type: Array, required: true }
  },
  setup(_, { emit }) {
    const imageDirectory = process.env.VUE_APP_GAZETTE_IMG_DIRECTORY;
    const loading = ref({});
    const showConfirmDelete = ref(false);
    const postToDelete = ref(null);

    const statusClass = (status) => {
      if (status === "Draft") return "bg-gray-300 text-gray-800";
      if (status === "Published") return "bg-green-200 text-green-800";
      if (status === "Archived") return "bg-yellow-200 text-yellow-900";
      return "bg-gray-200 text-gray-700";
    };

    const formatStatus = (status) => {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };

    const togglePin = async (post) => {
      loading.value[post.id] = true;
      try {
        await updateModulePost(post.id, { isPinned: !post.isPinned });
        post.isPinned = !post.isPinned;
      } catch (err) {
        console.error("Failed to toggle pin:", err);
      } finally {
        loading.value[post.id] = false;
      }
    };

    // Delete functionality
    const openConfirmDelete = (post) => {
      postToDelete.value = post;
      showConfirmDelete.value = true;
    };
    const closeConfirmDelete = () => {
      showConfirmDelete.value = false;
      postToDelete.value = null;
    };
    const confirmDelete = async () => {
      if (!postToDelete.value) return;
      const post = postToDelete.value;
      loading.value[post.id] = true;
      try {
        await deleteModulePost(post.id);
        emit("delete", post.id);
      } catch (err) {
        console.error("Failed to delete post:", err);
      } finally {
        loading.value[post.id] = false;
        closeConfirmDelete();
      }
    };

    return {
      imageDirectory,
      statusClass,
      formatStatus,
      togglePin,
      loading,
      showConfirmDelete,
      postToDelete,
      openConfirmDelete,
      closeConfirmDelete,
      confirmDelete
    };
  }
};
</script>